import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';

const LOGIN_BG = "https://images.unsplash.com/photo-1556906781-9a412961c28c?w=1200&q=80";
const LOGO_URL = null; // Set to your company logo URL or place in /public/logo.png

export function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    let result;
    if (isLogin) {
      result = await login(email, password);
    } else {
      result = await register(email, password, name);
    }

    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden md:block bg-cover bg-center"
        style={{ backgroundImage: `url(${LOGIN_BG})` }}
      >
        <div className="h-full w-full bg-black/40 flex items-end p-12">
          <div>
            <h2 className="font-heading font-black text-4xl text-white tracking-tighter">
              FOOTWEAR<br />MANUFACTURING
            </h2>
            <p className="text-zinc-300 mt-2 text-sm">
              PO & Dispatch Management System
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            {LOGO_URL && <img src={LOGO_URL} alt="Logo" className="w-12 h-12" />}
            <div>
              <h1 className="font-heading font-black text-xl tracking-tighter text-[#09090B]">
                FOOTWEAR
              </h1>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                PO Management
              </p>
            </div>
          </div>

          {/* Title */}
          <h2 className="font-heading font-bold text-2xl text-[#09090B] mb-1">
            {isLogin ? 'Welcome back' : 'Create account'}
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            {isLogin ? 'Enter your credentials to access the system' : 'Register to get started'}
          </p>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="auth-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-zinc-500">
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 h-10 rounded-sm border-zinc-300 focus:ring-2 focus:ring-black focus:border-black"
                  placeholder="John Doe"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-10 rounded-sm border-zinc-300 focus:ring-2 focus:ring-black focus:border-black"
                placeholder="admin@footwear.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-10 rounded-sm border-zinc-300 focus:ring-2 focus:ring-black focus:border-black"
                placeholder="Enter password"
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-10 bg-[#09090B] text-white hover:bg-[#27272A] rounded-sm font-medium"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>

          {/* Toggle */}
          <p className="mt-6 text-center text-sm text-zinc-500">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="ml-1 text-[#09090B] font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Register' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
